import type { QueryKey } from '@tanstack/react-query'

import { globalScope, keyStartsWith, schoolScope } from '@/lib/query/scope'

type ScreenFeatureResource = 'screen' | 'global'

/**
 * Query keys for Screen Features (web refactor 02, KTD8), in plan 07's shape.
 *
 * The `schoolId` in a school key is the TARGET school, the one the request sends
 * as `school_id`, so the key and the request always agree. Fleet defaults (the
 * `SystemConfiguration` row with no school) live under the global scope.
 */
export const screenFeatureKeys = {
  all: (schoolId: string | null) => [...schoolScope(schoolId), 'screen-features'] as const,
  resource: (schoolId: string | null, r: ScreenFeatureResource) => [...screenFeatureKeys.all(schoolId), r] as const,
  entry: (schoolId: string | null, r: ScreenFeatureResource, name: string) =>
    [...screenFeatureKeys.resource(schoolId, r), name] as const,
  systemAll: () => [...globalScope, 'screen-features'] as const,
  system: (r: ScreenFeatureResource) => [...screenFeatureKeys.systemAll(), r] as const,
  systemEntry: (r: ScreenFeatureResource, name: string) => [...screenFeatureKeys.system(r), name] as const,
  /**
   * The super-admin app-configuration page's whole-resource read. With no school
   * its request carries no `school_id` and reads the fleet-default row, so the
   * key is the fleet one; with a school it is that school's resource.
   */
  adminResource: (schoolId: string | undefined, r: ScreenFeatureResource) =>
    schoolId === undefined ? screenFeatureKeys.system(r) : screenFeatureKeys.resource(schoolId, r)
}

// The one cross-scope match: school entries and fleet defaults share no prefix,
// so a fleet-default save invalidates both through this predicate. It lives here
// because lib/query/** may read key positions.
export const isAnyScreenFeatureKey = (key: QueryKey) =>
  keyStartsWith(key, screenFeatureKeys.systemAll()) || (key[0] === 'school' && key[2] === 'screen-features')
